import React from 'react';
import { Route, IndexRoute } from 'react-router';
import loadable from '@loadable/component';

import {
    checkAccountStatus,
    checkEmailExists,
    setUpForm,
    setUpSocialForm,
    checkPasswordReset,
} from '../common/utils';
import { setEmail } from '../common/actions/auth';

import {
    CHECKOUT_PATH,
    RESET_PASSWORD_STATUS_PATH,
    SOFT_ACCOUNT_STATUS_PATH,
    LOG_IN_PATH,
    SIGN_UP_PATH,
    SOCIAL_SIGN_UP_PATH,
    SOCIAL_LOOK_UP_PATH,
    SOCIAL_LOG_IN_PATH,
    LINK_ACCOUNTS_LOG_IN_PATH,
    CONFIRMATION_PATH,
    BASE_URL,
    RESET_PASSWORD_PATH,
    SET_PASSWORD_PATH,
    SET_LINKED_SOFT_ACCOUNTS_PATH,
    STATUS_PATH,
    LINK_SOCIAL_ACCOUNT_PATH,
    PASSWORDLESS_PAGE_PATH,
} from '../common/constants';

import Page from './Page';

const ConnectedCheckoutSignInScreen = loadable(() =>
    import(
        '../common/containers/checkoutSignInLite/ConnectedCheckoutSignInScreen'
    ),
);

const ConnectedLoginPage = loadable(() => import('../common/containers/login'));
const ConnectedSignupPage = loadable(() =>
    import('../common/containers/signup'),
);
const ConnectedResetPasswordPage = loadable(() =>
    import('../common/containers/resetPassword'),
);
const ConnectedSetPasswordPage = loadable(() =>
    import('../common/containers/setPassword'),
);
const ConnectedSocialLoginPage = loadable(() =>
    import('../common/containers/socialLogin/ConnectedSocialLoginPage'),
);
const ConnectedSocialSignupPage = loadable(() =>
    import('../common/containers/socialSignup'),
);
const ConnectedLinkAccountsLoginPage = loadable(() =>
    import('../common/containers/linkAccountsLogin'),
);
const ConnectedSetLinkedSoftAccountPage = loadable(() =>
    import('../common/containers/setLinkedSoftAccounts'),
);
const ConnectedSoftAccountStatusPage = loadable(() =>
    import('../common/containers/softAccountStatus'),
);
const ConnectedResetPasswordStatusPage = loadable(() =>
    import('../common/containers/resetPasswordStatus'),
);
const ConnectedConfirmationPage = loadable(() =>
    import('../common/containers/confirmation'),
);
const ConnectedSocialLookupPage = loadable(() =>
    import('../common/containers/socialLookup'),
);
const ConnectedSocialLinkedAccountPage = loadable(() =>
    import('../common/containers/socialLinkedAccount'),
);
const ConnectedPasswordlessPage = loadable(() =>
    import('../common/containers/passwordless'),
);

const getRoutes = (pageInfo, store) => {
    const PageWithInfo = (props) => <Page {...pageInfo} {...props} />;

    const _checkAccountStatus = (nextRouterState, replace) => {
        checkAccountStatus(store, replace);
    };

    const setUpSocialSingUpFormOnEnter = (nextRouterState, replace) => {
        setUpSocialForm(store, replace);
    };

    const setUpFormOnEnter = async ({ location: { query } }) => {
        if (query.email) {
            store.dispatch(setEmail(query.email));
        }

        setUpForm(store);
    };

    const checkSocialToken = (nextRouterState, replace) => {
        const {
            social: {
                socialData: { apple: { accessToken } = {} },
            },
        } = store.getState();

        if (!accessToken) {
            replace(BASE_URL);
        }
    };

    const setUpSocialLinkFormOnEnter = ({ location: { query } }, replace) => {
        const {
            social: {
                socialData: { apple: { email } = {} },
            },
        } = store.getState();

        if (email) {
            store.dispatch(setEmail(email));
        } else {
            replace(BASE_URL);
        }

        setUpForm(store);
    };

    const checkEmailOnEnter = (nextRouterState, replace) => {
        checkEmailExists(store, replace);
    };

    const checkResetOnEnter = ({ location: { query, pathname } }, replace) => {
        checkPasswordReset(store, query, replace, pathname);
    };

    return (
        <Route path={BASE_URL} component={PageWithInfo}>
            <IndexRoute
                component={ConnectedLoginPage}
                onEnter={setUpFormOnEnter}
            />

            <Route path={SIGN_UP_PATH} component={ConnectedSignupPage} />

            <Route
                path={CHECKOUT_PATH}
                component={ConnectedCheckoutSignInScreen}
                onEnter={setUpFormOnEnter}
            >
                <Route
                    path={LOG_IN_PATH}
                    component={ConnectedCheckoutSignInScreen}
                    onEnter={_checkAccountStatus}
                />
                <Route
                    path={SOCIAL_LOG_IN_PATH}
                    component={ConnectedCheckoutSignInScreen}
                />
            </Route>

            <Route
                path={CONFIRMATION_PATH}
                component={ConnectedConfirmationPage}
            />
            <Route
                path={SOCIAL_SIGN_UP_PATH}
                component={ConnectedSocialSignupPage}
                onEnter={setUpSocialSingUpFormOnEnter}
            />
            <Route
                path={SOCIAL_LOOK_UP_PATH}
                component={ConnectedSocialLookupPage}
                onEnter={checkSocialToken}
            />
            <Route
                path={SOCIAL_LOG_IN_PATH}
                component={ConnectedSocialLoginPage}
                onEnter={_checkAccountStatus}
            />

            <Route
                path={LINK_ACCOUNTS_LOG_IN_PATH}
                component={ConnectedLinkAccountsLoginPage}
                onEnter={_checkAccountStatus}
            />
            <Route
                path={SET_LINKED_SOFT_ACCOUNTS_PATH}
                component={ConnectedSetLinkedSoftAccountPage}
                onEnter={checkEmailOnEnter}
            />
            <Route
                path={RESET_PASSWORD_PATH}
                component={ConnectedResetPasswordPage}
                onEnter={checkResetOnEnter}
            />
            <Route
                path={PASSWORDLESS_PAGE_PATH}
                component={ConnectedPasswordlessPage}
                onEnter={checkResetOnEnter}
            />
            <Route
                path={SET_PASSWORD_PATH}
                component={ConnectedSetPasswordPage}
                onEnter={checkResetOnEnter}
            />
            <Route
                path={LINK_SOCIAL_ACCOUNT_PATH}
                component={ConnectedSocialLinkedAccountPage}
            />
            <Route path={STATUS_PATH}>
                <Route
                    path={SOFT_ACCOUNT_STATUS_PATH}
                    component={ConnectedSoftAccountStatusPage}
                />
                <Route
                    path={RESET_PASSWORD_STATUS_PATH}
                    component={ConnectedResetPasswordStatusPage}
                />
            </Route>
        </Route>
    );
};

export default getRoutes;
